import React from 'react';
import { Button, DatePicker, Divider, Form, message, Select, Space, Spin, Tag } from 'antd';
import { FormInstance } from 'antd/lib/form';
import moment, { Moment } from 'moment';
import api, {
  GetFoodCardRecordMonthlySummaryXlsxRes,
  GetKioskVendingListParameters,
  GetKioskVendingRes,
} from '../../api';


interface PageState {
  kioskVendings: GetKioskVendingRes[]
  loading: boolean
  exporting: boolean
}


interface FormValues {
  monthRange: [Moment, Moment]
  kioskVendingIds: string[]
}


interface Props {
  onFinish?(value: GetFoodCardRecordMonthlySummaryXlsxRes): void
}


const { RangePicker } = DatePicker;


class FoodCardRecordMonthlySummaryExportXlsx extends React.Component<Props, PageState> {
  formRef = React.createRef<FormInstance>()

  constructor(props: Props) {
    super(props);
    this.state = {
      kioskVendings: [],
      loading: true,
      exporting: false,
    };
  }

  componentDidMount() {
    this._loadKioskVendings();
  }

  _getKioskVendingList = async (params: GetKioskVendingListParameters) => {
    const res = await api.GetKioskVendingList(params);
    if (res.kind !== 'ok') throw new Error(res.kind);
    return res.data;
  }

  _loadKioskVendings = async () => {
    try {
      const kioskVendingList = await this._getKioskVendingList({ count: 1000 });
      const kioskVendings = kioskVendingList.data;

      this.setState({ kioskVendings, loading: false }, () => {
        this.formRef.current?.setFieldsValue({
          kioskVendingIds: kioskVendings.map(kioskVending => kioskVending.id),
        });
      });
    } catch (error) {
      this.setState({ loading: false });
      message.error('讀取取餐機失敗');
    }
  }

  _onFinish = async (values: FormValues) => {
    const [beginMonth, endMonth] = values.monthRange;

    try {
      this.setState({ exporting: true });
      const res = await api.GetFoodCardRecordMonthlySummaryXlsx({
        beginMonth: beginMonth.format('YYYY-MM'),
        endMonth: endMonth.format('YYYY-MM'),
        kioskVendingIds: values.kioskVendingIds,
      });

      if (res.kind !== 'ok') throw new Error(res.kind);
      const data = (res as { kind: 'ok', data: GetFoodCardRecordMonthlySummaryXlsxRes }).data;
      this.props.onFinish && this.props.onFinish(data);
    } catch (error) {
      message.error('導出失敗，請稍後再試');
    } finally {
      this.setState({ exporting: false });
    }
  }

  _selectAllKioskVendings = () => {
    this.formRef.current?.setFieldsValue({
      kioskVendingIds: this.state.kioskVendings.map(kioskVending => kioskVending.id),
    });
  }

  _clearKioskVendings = () => {
    this.formRef.current?.setFieldsValue({ kioskVendingIds: [] });
  }

  _getKioskName = (kioskVending: GetKioskVendingRes) => {
    if (typeof kioskVending.name === 'string') return kioskVending.name;
    return kioskVending.name['zh-tw'] || kioskVending.name['zh-cn'] || kioskVending.name.en || '---';
  }

  render() {
    const defaultMonth = moment().subtract(1, 'month').startOf('month');

    return (
      <Spin spinning={this.state.loading}>
        <Form<FormValues>
          ref={this.formRef}
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 17 }}
          initialValues={{
            monthRange: [defaultMonth, defaultMonth],
            kioskVendingIds: [],
          }}
          onFinish={this._onFinish}
        >
          <Form.Item
            name="monthRange"
            label="統計月份"
            extra="可選擇本月，資料將統計至匯出時間。"
            rules={[{ required: true, message: '請選擇統計月份' }]}
          >
            <RangePicker
              picker="month"
              format="YYYY-MM"
              style={{ width: '100%' }}
              disabledDate={(current) => current && current > moment().endOf('month')}
            />
          </Form.Item>

          <Form.Item
            name="kioskVendingIds"
            label="取餐機"
            rules={[{ required: true, type: 'array', min: 1, message: '請至少選擇一部取餐機' }]}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount={3}
              placeholder="請選擇取餐機"
              optionLabelProp="optionLabel"
              filterOption={(input, option) => {
                const kioskVending = this.state.kioskVendings.find(item => item.id === option?.value);
                return !!kioskVending && `${this._getKioskName(kioskVending)} ${kioskVending.code}`
                  .toLowerCase()
                  .includes(input.toLowerCase());
              }}
              dropdownRender={menu => (
                <div>
                  <Space style={{ padding: '4px 8px' }}>
                    <Button type="link" size="small" onClick={this._selectAllKioskVendings}>全選</Button>
                    <Button type="link" size="small" onClick={this._clearKioskVendings}>清除</Button>
                  </Space>
                  <Divider style={{ margin: '4px 0' }} />
                  {menu}
                </div>
              )}
              options={this.state.kioskVendings.map(kioskVending => ({
                value: kioskVending.id,
                optionLabel: `${this._getKioskName(kioskVending)} (${kioskVending.code})`,
                label: (
                  <Space>
                    <Tag color="orange">{this._getKioskName(kioskVending)}</Tag>
                    <Tag color="blue">{kioskVending.code}</Tag>
                  </Space>
                ),
              }))}
            />
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 5 }}>
            <Button type="primary" htmlType="submit" loading={this.state.exporting}>
              導出 Excel
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    );
  }
}


export default FoodCardRecordMonthlySummaryExportXlsx;
